<template>
  <top-navigation
    :title="$t('settings.roles')"
    :button-name="isAdmin ? $t('global.inputs.labels.save') : 'button'"
    :loading="status.loading"
    :disabled="!madeChanges"
    @submit-click="update()"
  >
  </top-navigation>
  <div class="mx-auto min-h-screen px-3 pb-6 pt-28">
    <!-- Info Banner -->
    <information-text
      v-if="isAdmin"
      class="mb-3"
      color="blue"
      :information="$t('settings.permissions.as_admin_you_can_change_roles')"
    />
    <information-text
      v-else
      class="mb-3"
      color="orange"
      :information="
        $t('settings.permissions.as_non_admin_you_cannot_change_roles')
      "
    />

    <!-- Role Explanation -->
    <information-text class="mb-3" color="yellow">
      <div class="space-y-1.5">
        <div class="flex items-start gap-1.5">
          <span>👑</span>
          <div>
            <strong>{{ $t("roles.admin") }}:</strong>
            {{ $t("settings.permissions.admin_description") }}
          </div>
        </div>
        <div class="flex items-start gap-1.5">
          <span>⭐</span>
          <div>
            <strong>{{ $t("roles.organiser") }}:</strong>
            {{ $t("settings.permissions.organiser_description") }}
          </div>
        </div>
        <div class="flex items-start gap-1.5">
          <span>👤</span>
          <div>
            <strong>{{ $t("roles.member") }}:</strong>
            {{ $t("settings.permissions.member_description") }}
          </div>
        </div>
      </div>
    </information-text>

    <!-- Members List -->
    <div class="space-y-2.5">
      <div
        v-for="member in membersSorted"
        :key="member.uuid"
        class="rounded-lg border-2 border-black-dark bg-white p-2.5 shadow-indigo-hard-sm transition-all dark:bg-kekkle-dark-light"
        :class="{
          'opacity-60': !isAdmin && member.uuid !== userUuid,
        }"
      >
        <!-- Member Header -->
        <div class="mb-2 flex items-center gap-2">
          <small-user-thumb :uuid="member.uuid" />
          <div class="flex-1">
            <p class="text-sm font-bold text-gray-900 dark:text-slate-200">
              {{ member.name }}
              <span
                v-if="member.uuid === userUuid"
                class="ml-1 text-xs font-normal text-gray-500 dark:text-slate-400"
              >
                ({{ $t("settings.permissions.you") }})
              </span>
            </p>
          </div>
        </div>

        <!-- Role Selector -->
        <div class="grid grid-cols-3 gap-1.5">
          <!-- Admin Role -->
          <button
            type="button"
            :disabled="
              !isAdmin ||
              (member.permission === 'admin' && member.uuid !== userUuid)
            "
            class="group relative overflow-hidden rounded-lg border-2 border-black-dark p-2 text-center transition-all active:translate-x-[1px] active:translate-y-[1px]"
            :class="
              visiblePermission[member.uuid] === 'admin'
                ? 'bg-kekkle-yellow shadow-yellow-hard-sm'
                : 'bg-white hover:bg-gray-50 dark:bg-kekkle-dark dark:hover:bg-kekkle-dark-light'
            "
            @click="setModelData(member.uuid, 'admin')"
          >
            <div
              v-if="visiblePermission[member.uuid] === 'admin'"
              class="absolute right-1 top-1"
            >
              <span>✓</span>
            </div>
            <div class="text-2xl">👑</div>
            <div
              class="mt-1 text-xs font-bold"
              :class="
                visiblePermission[member.uuid] === 'admin'
                  ? 'text-gray-900'
                  : 'text-gray-600 dark:text-slate-400'
              "
            >
              {{ $t("roles.admin") }}
            </div>
          </button>

          <!-- Organiser Role -->
          <button
            type="button"
            :disabled="
              !isAdmin ||
              (member.permission === 'admin' && member.uuid !== userUuid)
            "
            class="group relative overflow-hidden rounded-lg border-2 border-black-dark p-2 text-center transition-all active:translate-x-[1px] active:translate-y-[1px]"
            :class="
              visiblePermission[member.uuid] === 'organiser'
                ? 'bg-kekkle-blue shadow-blue-hard-sm'
                : 'bg-white hover:bg-gray-50 dark:bg-kekkle-dark dark:hover:bg-kekkle-dark-light'
            "
            @click="setModelData(member.uuid, 'organiser')"
          >
            <div
              v-if="visiblePermission[member.uuid] === 'organiser'"
              class="absolute right-1 top-1"
            >
              <span>✓</span>
            </div>
            <div class="text-2xl">⭐</div>
            <div
              class="mt-1 text-xs font-bold"
              :class="
                visiblePermission[member.uuid] === 'organiser'
                  ? 'text-gray-900'
                  : 'text-gray-600 dark:text-slate-400'
              "
            >
              {{ $t("roles.organiser") }}
            </div>
          </button>

          <!-- Member Role -->
          <button
            type="button"
            :disabled="
              !isAdmin ||
              (member.permission === 'admin' && member.uuid !== userUuid)
            "
            class="group relative overflow-hidden rounded-lg border-2 border-black-dark p-2 text-center transition-all active:translate-x-[1px] active:translate-y-[1px]"
            :class="
              visiblePermission[member.uuid] === 'member'
                ? 'bg-kekkle-purple shadow-purple-hard-sm'
                : 'bg-white hover:bg-gray-50 dark:bg-kekkle-dark dark:hover:bg-kekkle-dark-light'
            "
            @click="setModelData(member.uuid, 'member')"
          >
            <div
              v-if="visiblePermission[member.uuid] === 'member'"
              class="absolute right-1 top-1"
            >
              <span>✓</span>
            </div>
            <div class="text-2xl">👤</div>
            <div
              class="mt-1 text-xs font-bold"
              :class="
                visiblePermission[member.uuid] === 'member'
                  ? 'text-gray-900'
                  : 'text-gray-600 dark:text-slate-400'
              "
            >
              {{ $t("roles.member") }}
            </div>
          </button>
        </div>

        <!-- Warning for protected admin -->
        <description-box
          v-if="
            !isAdmin ||
            (member.permission === 'admin' && member.uuid !== userUuid)
          "
          size="sm"
          class="mt-2"
        >
          <div class="flex items-center gap-2">
            <span>🔒</span>
            <p class="text-gray-600 dark:text-slate-400">
              {{
                member.uuid === userUuid
                  ? $t("settings.permissions.you_cannot_change_others")
                  : $t("settings.permissions.admin_role_protected")
              }}
            </p>
          </div>
        </description-box>
      </div>
    </div>
  </div>
</template>
<script>
import TopNavigation from "../../components/TopNavigation.vue";
import SmallUserThumb from "../../components/SmallUserThumb.vue";
import InformationText from "../../components/InformationText.vue";
import DescriptionBox from "../../components/ui/DescriptionBox.vue";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";

import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "@/helpers/firebase/firebase";

import * as Sentry from "@sentry/vue";
import {
  setErrorToast,
  setSuccessToast,
  setWarningToast,
} from "@/helpers/notifications/toast";

export default {
  name: "MemberPermissionsView",
  components: {
    SmallUserThumb,
    TopNavigation,
    InformationText,
    DescriptionBox,
  },
  setup() {
    const authStore = useAuthStore();
    const groupsStore = useGroupsStore();
    const userStore = useUserStore();
    return { authStore, groupsStore, userStore };
  },
  data: function () {
    return {
      status: {
        loading: false,
      },
      visiblePermission: {},
    };
  },
  computed: {
    membersSorted() {
      return this.groupsStore.activeGroupMembersSorted;
    },
    members() {
      return this.groupsStore.groupData && this.groupsStore.groupData.members
        ? this.groupsStore.groupData.members
        : {};
    },
    groupDocId() {
      return this.userStore.activeGroup;
    },
    isAdmin() {
      return this.groupsStore.isAdmin;
    },
    userUuid() {
      return this.authStore.authUuid;
    },
    madeChanges() {
      for (const [uuid, value] of Object.entries(this.members)) {
        if (this.visiblePermission[uuid] !== value["permission"]) {
          return true;
        }
      }
      return false;
    },
  },
  created() {
    for (const [key, value] of Object.entries(this.members)) {
      this.visiblePermission[key] = value.permission;
    }
  },
  methods: {
    setModelData(uuid, permission) {
      this.visiblePermission[uuid] = permission;
    },
    async update() {
      this.status.loading = true;

      let permissions = {};
      for (const [uuid, value] of Object.entries(this.members)) {
        if (this.visiblePermission[uuid] !== value["permission"]) {
          permissions[uuid] = this.visiblePermission[uuid];
        }
      }

      let payload = {};
      payload["groupId"] = this.groupDocId;
      payload["permissions"] = permissions;

      try {
        const changeGroupPermission = httpsCallable(
          firebaseFunctions,
          "changeGroupPermission",
        );
        await changeGroupPermission(payload);
        setSuccessToast({
          message: this.$t("global.toast.saved_successfully"),
        });
        await this.groupsStore.refreshAllUserGroupsData();
        this.$router.push({ name: "settings" });
      } catch (e) {
        switch (e.message) {
          case "User has not enough permission":
            setWarningToast({
              message: this.$t("settings.not_authorized"),
            });
            break;
          case "Needs one admin":
            setWarningToast({
              message: this.$t("global.toast.error_one_admin_minimum"),
            });
            break;
          default:
            setErrorToast({
              message: this.$t("global.toast.error_changing_permissions"),
            });
            Sentry.captureException(e);
            break;
        }
      } finally {
        this.status.loading = false;
      }
    },
  },
};
</script>
